import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PlayCircle, CheckCircle, Clock, Calendar, Eye,
  Dumbbell, Activity, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';
import api from '../services/api';
import { toast } from 'sonner';
import { scoreBucket, toneClass, toneHex } from '../utils/coachLanguage';

const Sessions = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchSessions(); }, []);

  const fetchSessions = async () => {
    try {
      const response = await api.getSessions();
      setSessions(response.data);
    } catch (error) {
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (s) => new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const formatTime = (s) => new Date(s).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  const handleDeleteSession = async (sessionId) => {
    try {
      await api.deleteSession(sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
      toast.success('Session deleted');
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const StatusBadge = ({ session }) => session.completed_at ? (
    <span className="flex items-center gap-1 bg-green-500/20 text-green-500 px-2 py-1 rounded-sm text-xs">
      <CheckCircle className="w-3 h-3" /> Completed
    </span>
  ) : (
    <span className="flex items-center gap-1 bg-yellow-500/20 text-yellow-500 px-2 py-1 rounded-sm text-xs">
      <Clock className="w-3 h-3" /> In Progress
    </span>
  );

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-secondary font-bold tracking-tight">
              WORKOUT <span className="text-primary">SESSIONS</span>
            </h1>
            <p className="text-zinc-400 mt-1">View your workout history and analysis results</p>
          </div>
          <Button className="hpme-btn-primary" onClick={() => navigate('/workouts')} data-testid="new-session-btn">
            <PlayCircle className="w-5 h-5 mr-2" /> New Session
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="text-center">
              <Activity className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
              <p className="text-zinc-400">Loading sessions...</p>
            </div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="hpme-card p-12 text-center">
            <Dumbbell className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
            <h3 className="font-secondary font-bold text-xl mb-2">NO SESSIONS YET</h3>
            <p className="text-zinc-400 mb-6">Start a workout to see your session history</p>
            <Button className="hpme-btn-primary" onClick={() => navigate('/workouts')}>Browse Workouts</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {sessions.map(session => {
              const a = session.summary || {};
              const breakdown = a.narrative?.exercise_breakdown || [];
              const overall = (a.overall_score !== null && a.overall_score !== undefined) ? Math.round(a.overall_score) : null;
              const overallBucket = scoreBucket(overall);
              const formScore = (a.form_score !== null && a.form_score !== undefined) ? Math.round(a.form_score) : null;
              const formBucket = scoreBucket(formScore);

              return (
                <div
                  key={session.id}
                  className="hpme-card p-5 border-l-4 group"
                  style={{ borderLeftColor: overall !== null ? toneHex(overallBucket.tone) : '#3f3f46' }}
                  data-testid={`session-row-${session.id}`}
                >
                  <div className="hpme-card-corner" />

                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-zinc-800 rounded-sm flex items-center justify-center flex-shrink-0">
                        <Dumbbell className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-secondary font-bold text-lg tracking-tight">{session.workout_name}</h3>
                        <div className="flex items-center gap-4 text-sm text-zinc-500 mt-1">
                          <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{formatDate(session.started_at)}</span>
                          <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{formatTime(session.started_at)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {overall !== null && (
                        <div className="text-right hidden md:block" data-testid={`session-overall-${session.id}`}>
                          <p className={`font-secondary font-bold text-lg ${toneClass(overallBucket.tone)}`}>
                            {overallBucket.label}{' '}
                            <span className="text-zinc-400 font-mono text-sm ml-1">{overall}/100</span>
                          </p>
                          <p className="text-xs text-zinc-500">OVERALL</p>
                        </div>
                      )}
                      {formScore !== null && (
                        <div className="text-right hidden md:block" data-testid={`session-form-${session.id}`}>
                          <p className={`font-secondary font-bold text-lg ${toneClass(formBucket.tone)}`}>
                            {formBucket.label}{' '}
                            <span className="text-zinc-400 font-mono text-sm ml-1">{formScore}/100</span>
                          </p>
                          <p className="text-xs text-zinc-500">FORM</p>
                        </div>
                      )}
                      <StatusBadge session={session} />
                      <Link to={`/sessions/${session.id}`} data-testid={`view-session-${session.id}`}>
                        <Button variant="ghost" className="text-zinc-400 hover:text-primary"><Eye className="w-5 h-5" /></Button>
                      </Link>
                      <Button
                        variant="ghost"
                        className="text-zinc-400 hover:text-red-400"
                        onClick={() => handleDeleteSession(session.id)}
                        data-testid={`delete-session-${session.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {breakdown.length > 0 ? (
                    <div className="mt-4 pt-4 border-t border-zinc-800 space-y-2" data-testid={`session-exercise-verdicts-${session.id}`}>
                      {breakdown.map((b, idx) => {
                        const s = (b.score !== null && b.score !== undefined) ? Math.round(b.score) : null;
                        const bk = scoreBucket(s);
                        return (
                          <div key={idx} className="flex items-center justify-between gap-3 text-sm" data-testid={`session-verdict-${session.id}-${idx}`}>
                            <span className="text-zinc-300 font-medium truncate">{b.exercise || 'Exercise'}</span>
                            <span className={`font-secondary font-bold ${toneClass(bk.tone)} whitespace-nowrap`}>
                              {bk.label}
                              {s !== null && (
                                <>
                                  {' '}
                                  <span className="text-zinc-500 font-mono text-xs ml-2">{s}/100</span>
                                </>
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : session.completed_at ? (
                    <div className="mt-4 pt-4 border-t border-zinc-800 text-sm text-zinc-500" data-testid={`session-no-camera-${session.id}`}>
                      No camera reps captured — start your camera next time for detailed form feedback.
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Sessions;
